








































// Vue
import Vue from "vue";
// DayPilot
import { DayPilot, DayPilotScheduler } from "daypilot-pro-vue";
// moment.js
import moment from "moment";
import AdminService from "@/views/admin/Admin.service";
import { AuthUser } from "@/core/_models/authUser.model";
import { ClientModel } from "@/core/api/apiClient";
import { Component, Prop } from "vue-property-decorator";
import { DayOfWeek } from "@/core/_constants/daysOfWeek.constant";

// note:  using class-style components for full typescript support
@Component({
  name: "Scheduler",
  components: { DayPilotScheduler }
})
export default class Scheduler extends Vue {
  // Props
  @Prop({ required: true, type: Object }) readonly config!: any;

  // Data
  intervalTimeMarker: any = null;
  timeIntervals = [
    { text: "15 minute", value: 15 },
    { text: "30 minute", value: 30 },
    { text: "60 minute", value: 60 }
  ];
  selectedTimeInterval = 30;

  // Vuex Getter
  get currentUser() {
    return (this as any).$store.getters.currentUser;
  }

  // Computed
  get scheduler(): any {
    return (this.$refs.scheduler as any).control;
  }

  get isToday(): boolean {
    const dateScheduler = new Date(this.config.startDate.value);
    const dateToday = new Date();
    return (
      dateScheduler.getFullYear() === dateToday.getFullYear() &&
      dateScheduler.getMonth() === dateToday.getMonth() &&
      dateScheduler.getDate() === dateToday.getDate()
    );
  }

  get isWorkingDaysSet(): boolean {
    return this.currentUser.client.workingDays?.length > 0;
  }

  get sortedWorkingDays(): number[] {
    return [...this.currentUser.client.workingDays]
      .map((day) => DayOfWeek[day as keyof typeof DayOfWeek]) // "Monday" -> 1
      .sort((a, b) => a - b);
  }

  // Lifecycle
  mounted() {
    this.init();
  }

  destroyed() {
    clearInterval(this.intervalTimeMarker);
  }
  // Methods
  init() {
    this.selectedTimeInterval = this.currentUser.client.jobSchedulerTimeIntervals;
    Vue.set(this.config, "cellDuration", this.selectedTimeInterval);
    this.scheduler.update();

    this.schedulerTimeIndicator();

    Vue.nextTick(() => {
      this.scrollToTimeToday(7);
    });
  }

  schedulerTimeIndicator() {
    // Red "time indicator" on the Scheduler
    const markerDate = new DayPilot.Date(
      moment(new Date()).subtract(this.getCurrentOffset(), "hours").toDate().toISOString()
    );
    this.scheduler.separators = [{ color: "red", location: markerDate }];

    this.intervalTimeMarker = setInterval(() => {
      const markerDate = new DayPilot.Date(
        moment(new Date()).subtract(this.getCurrentOffset(), "hours").toDate().toISOString()
      );

      this.scheduler.update({
        separators: [{ color: "red", location: markerDate }]
      });
    }, 60000);
  }

  scrollToTimeToday(hour: number) {
    const dateTime = DayPilot.Date.today().addHours(hour);
    this.scheduler.scrollTo(dateTime);
  }

  async updateTimeIntervals() {
    const currentUser = this.currentUser as AuthUser;

    // Build payload
    const payload = new ClientModel({
      clientId: currentUser.client!.clientId,
      name: currentUser.client!.name,
      companyLogoUrl: currentUser.client!.companyLogoUrl,
      mainThemeColor: currentUser.client!.mainThemeColor,
      mainThemeTextColor: currentUser.client!.mainThemeTextColor,
      jobSchedulerTimeIntervals: this.selectedTimeInterval
    });

    await AdminService.updateClient(this.currentUser.clientId, payload);
    this.$store.dispatch("SET_JOBSCHEDULER_TIME_INTERVALS", this.selectedTimeInterval);

    Vue.set(this.config, "cellDuration", this.selectedTimeInterval);
    this.scheduler.update();
  }

  previous() {
    let addDays = -1;
    if (this.isWorkingDaysSet) {
      addDays = this.getDaysToPreviousWorkDay();
    }

    const dpNewDate = this.config.startDate.addDays(addDays);

    Vue.set(this.config, "startDate", dpNewDate);
    this.$emit("get-jobs", dpNewDate.value);
    this.scheduler.update();
  }

  next() {
    let addDays = 1;

    if (this.isWorkingDaysSet) {
      addDays = this.getDaysToNextWorkDay();
    }
    const dpNewDate = this.config.startDate.addDays(addDays);
    Vue.set(this.config, "startDate", dpNewDate);
    this.$emit("get-jobs", dpNewDate.value);
    this.scheduler.update();
  }

  today() {
    if (this.isToday) return;

    // ! Offset
    const dpNewDate: any = new DayPilot.Date(
      moment(new Date()).subtract(this.getCurrentOffset(), "hours").toDate().toISOString()
    );
    Vue.set(this.config, "startDate", dpNewDate);
    this.$emit("get-jobs", dpNewDate.value);
    this.scheduler.update();
  }

  getCurrentOffset() {
    return Math.abs(moment().utcOffset() / 60);
  }

  clearSelection() {
    this.scheduler.clearSelection();
  }

  private getDaysToPreviousWorkDay(): number {
    const currentWeekDay = this.config.startDate.dayOfWeek();
    let addDays: number;

    let nextWeekDay = this.sortedWorkingDays
      .slice()
      .reverse()
      .find((x) => x < currentWeekDay);
    if (nextWeekDay !== undefined) {
      addDays = nextWeekDay - currentWeekDay;
    } else {
      nextWeekDay = this.sortedWorkingDays[this.sortedWorkingDays.length - 1];
      addDays = -currentWeekDay + nextWeekDay - 7;
    }

    return addDays;
  }

  private getDaysToNextWorkDay(): number {
    const currentWeekDay = this.config.startDate.dayOfWeek();
    let addDays: number;
    let nextWeekDay = this.sortedWorkingDays.find((x) => x > currentWeekDay);
    if (nextWeekDay !== undefined) {
      addDays = nextWeekDay - currentWeekDay;
    } else {
      nextWeekDay = this.sortedWorkingDays[0];
      addDays = nextWeekDay - currentWeekDay + 7;
    }

    return addDays;
  }
}
